import {
  DashboardState,
  EProjectType,
  EScreenType,
  ESdChangeReason,
  EUserAccessPart,
  IProcessStepScreensInfo,
  IRecentlyOpenedScreensDto,
  IScreenDesignInfo,
  IScreenFolder,
  ITreeItem,
  LeftPanelType,
  SpecialDIIds
} from "@kuika/kuika-cl-model";
import React from "react";
import {
  DragDropContext,
  Draggable,
  Droppable,
  type DraggableLocation,
  type DraggableProvided,
  type DraggableStateSnapshot,
  type DragUpdate,
  type DropResult,
  type DroppableProvided,
  type DroppableStateSnapshot
} from "react-beautiful-dnd";
import { connect } from "react-redux";
import { ScreenService } from "../../../../services/screen-service";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { getSdInstance } from "../../../../shared/utility/designer-context";
import {
  addDesignerItemToFolder,
  addDesignerItemToSubfolder,
  deleteDesignerFolder,
  deleteDesignerSubfolder,
  removeDesignerItemFromFolder,
  removeDesignerItemFromSubfolder
} from "../../../../shared/utility/designer-folder-actions";
import { FolderHelper } from "../../../../shared/utility/folder-helper";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { getScreenRoleIcon } from "../../../../shared/utility/screen-role-icon";
import {
  buildScreenActionMenu,
  canOpenDeleteScreenModal,
  deleteScreenFromRow,
  duplicateScreenFromRow,
  exportScreenKuikaFile,
  getScreenActionAccess,
  hasAnyScreenRowAction,
  renameCurrentScreen,
  type DesignType,
  type ScreenRenameOutcome
} from "../../../../shared/utility/screen-row-actions";
import { showDomainAccessRequiredModal } from "../../../../store/domain-access-modal/actions";
import { DeleteModal } from "../../../common/delete-modal";
import KuikaDropdownMenu, {
  type KuikaDropdownMenuItemType
} from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import { redirectToProcessForms } from "../../flows/process-editor/redirect-to-process-forms";
import { getTreeItemChildMenuItems } from "../../footer/breadcrumb/breadcrumb-menu-items";
import { ScreenFolderAddComponent } from "../../left-panel/screens-panel/screen-folder-add";
import { ReportCreateModal } from "../../report-designer/report-create-modal";
import "../../../ui-kit/kuika-entity-picker/kuika-entity-picker.scss";

export interface ISubBarScreenSelectorProps {
  showDomainAccessRequiredModal: (actionName: string | string[]) => void;
}

/**
 * Which section a chip anchors to. Mirrors the screens panel's accordion taxonomy:
 * "screens" is the plain `EScreenType.Default` folder tree (in a mobile project those are the
 * MOBILE pages), while "web" is the distinct `EScreenType.WebScreen` type — the special
 * web-rendered screens a mobile project can carry. Labelling the Default tree "Web" (as this
 * selector originally did) erased that distinction.
 */
type ScreenFilterTab = "all" | "screens" | "web" | "workflow" | "master";

interface IScreenFilterTabDef {
  key: ScreenFilterTab;
  label: string;
}

// The chips are section ANCHORS, not filters — so only sections you can't already see deserve
// one. No "Recent" and no "Screens" chip: both sit at the very top of the body, which is exactly
// where "All" scrolls, and the tree root row carries the Screens count itself.
const SCREEN_FILTER_TABS: IScreenFilterTabDef[] = [
  { key: "all", label: "All" },
  { key: "web", label: "Web" },
  { key: "workflow", label: "Workflow" },
  { key: "master", label: "Master" }
];

// How many recent screens the RECENT band shows. The screen you are ON isn't one of them — it
// already owns the CURRENT card and the breadcrumb, so repeating it as a recent row is noise.
const RECENT_VISIBLE_COUNT = 4;
// Ask the server for one extra: the current screen usually tops the feed and gets dropped.
const RECENT_SCREEN_COUNT = RECENT_VISIBLE_COUNT + 1;

interface ISubBarScreenSelectorState {
  isOpen: boolean;
  searchText: string;
  expandedFolders: Record<string, boolean>;
  activeTab: ScreenFilterTab;
  recentScreens: IRecentlyOpenedScreensDto[];
  /**
   * Which row's hover menu ("…" on a screen or a folder, "+" on the project root) is open. Tracked
   * here rather than left to each dropdown's internal state because the trigger is only *rendered*
   * on row hover — moving the pointer into the open overlay leaves the row, and without this the
   * trigger would vanish under its own menu.
   */
  openRowMenuKey: string | null;
  /** Screen being renamed ("" = none); the editor itself lives in the current-screen card. */
  renamingScreenId: string;
  renameValue: string;
  isRenameLoading: boolean;
  /** Folder being renamed; drives the shared folder modal in edit mode. */
  renamingFolder: IScreenFolder | null;
  deleteTarget: DeleteTarget | null;
  isDeleteModalOpen: boolean;
  /** Report mode only: the "+" creates in place rather than sending you to the landing page. */
  isNewReportModalVisible: boolean;
  /** Hover list of the screen root's children on the screen name (KOA-18608). */
  isElementsOpen: boolean;
}

/** One delete modal serves both row kinds, so its target says which one asked for it. */
type DeleteTarget = { kind: "screen"; screen: IScreenDesignInfo } | { kind: "folder"; folder: IScreenFolder };

// Screen types that aren't switchable page targets in the row-2 breadcrumb.
const NON_PAGE_SCREEN_TYPES: EScreenType[] = [EScreenType.Master, EScreenType.Report, EScreenType.Component];
// Synthetic root node id (the project) that wraps all top-level folders + unfoldered screens.
const ROOT_ID = "__project_root__";
// Synthetic ids for the type groups that sit beside the folder tree.
const WORKFLOW_GROUP_ID = "__workflow__";
const MASTER_GROUP_ID = "__master__";
const WEB_GROUP_ID = "__webscreens__";
// Per-process sub-nodes inside the Workflow group (`prefix + processId`), matching the screens
// panel's per-workflow collapsibles. Prefixed so isFolderExpanded can default them to open.
const WF_PROCESS_KEY_PREFIX = "__wfprocess__";

/** One process's slice of the Workflow group — its workflow name and the screens bound to it. */
interface IWorkflowProcessGroup {
  processId: string;
  name: string;
  screens: IScreenDesignInfo[];
}
// Sections a screen row can be listed under. One screen is routinely rendered in more than one
// of them at the same time (RECENT plus its folder, Master plus RECENT), so the section is half
// of a row's identity — see screenRowKey.
const RECENT_SECTION_ID = "__recent__";
const SEARCH_SECTION_ID = "__search__";
const TREE_SECTION_ID = "__tree__";
const TREE_INDENT_PX = 16;
// From the row's edge; the list stands --gap-xs in from the panel's, so content starts 12px in.
const TREE_BASE_PADDING_PX = 8;
// Leaf screens have no chevron, so pad them by the chevron slot (12px icon + 4px
// row gap) to align their icon under the parent folder's icon at the same depth.
const TREE_LEAF_SPACER_PX = 16;
const RENAME_INPUT_TEST_ID = "subBarRenameInput";
// How long a drag has to dwell on a collapsed folder before it springs open (panel parity).
const DROP_HOVER_EXPAND_MS = 350;
// The panel's id for "belongs to no folder"; kept identical so the two read as one contract.
const UNFOLDERED_DROPPABLE_ID = "droppable2";

/**
 * Row-2 breadcrumb: `project > <screen> ▾`. The screen dropdown is a folder tree (matching the
 * screens-panel data): a current-screen card + search on top, a nested folder/screen tree in the
 * body, and a "N screens · M folders" + Manage-all-screens footer. Switching reuses
 * sd.saveScreen + sd.loadScreen.
 */
class SubBarScreenSelector extends PureSdComponent<
  ISubBarScreenSelectorProps & IWithSDProps & IWithContextProp,
  ISubBarScreenSelectorState
> {
  state: ISubBarScreenSelectorState = {
    isOpen: false,
    searchText: "",
    expandedFolders: {},
    activeTab: "all",
    recentScreens: [],
    openRowMenuKey: null,
    renamingScreenId: "",
    renameValue: "",
    isRenameLoading: false,
    renamingFolder: null,
    deleteTarget: null,
    isDeleteModalOpen: false,
    isNewReportModalVisible: false,
    isElementsOpen: false
  };

  /**
   * Scroll target for a section chip. Looked up in the DOM rather than held in a ref:
   * rc-menu renders the menu body twice (a hidden off-screen copy it measures with), and
   * a React ref binds to whichever rendered last — which was the copy at x=-9998, so
   * scrolling silently did nothing. Pick the on-screen one.
   */
  private findSectionElement = (tab: ScreenFilterTab): HTMLElement | null => {
    const candidates: HTMLElement[] = Array.from(
      document.querySelectorAll<HTMLElement>(`[data-subbar-section="${tab}"]`)
    );
    // right > 0 rather than left > 0: the real panel can legitimately sit at x=0, while
    // the measurement copy lives at x=-9998 and so ends far left of the viewport.
    return candidates.find((el: HTMLElement) => el.getBoundingClientRect().right > 0) ?? null;
  };

  componentDidMount(): void {
    // The screen list is only populated once the screens panel (or a save) runs; on a fresh
    // project open it can be empty, which would make the dropdown look non-functional. Preload it.
    this.loadScreenListIfNeeded();
    this.preloadRecentScreensOnce();
  }

  componentDidUpdate(): void {
    // The project usually arrives after we mount, and the recents feed is per user+project — so
    // the first chance to prefetch is here.
    this.preloadRecentScreensOnce();
  }

  /**
   * Project the recents list was last fetched for. Recents used to be fetched only when the
   * dropdown opened, so the RECENT section popped in a beat after the panel appeared (and shoved
   * the tree down with it). Prefetching keeps the panel's first paint complete; the fetch on open
   * still runs, but it now replaces an existing list instead of filling an empty one.
   */
  private recentsProjectId: string | null = null;

  private preloadRecentScreensOnce = (): void => {
    const projectId: string | undefined = this.props.sd.project?.id;
    if (!projectId || this.recentsProjectId === projectId) return;
    this.recentsProjectId = projectId;
    this.loadRecentScreens();
  };

  private loadScreenListIfNeeded = async (): Promise<void> => {
    const screenList: IScreenDesignInfo[] | undefined = this.props.sd.screenList;
    if (!screenList || screenList.length === 0) {
      await this.props.sd.setScreenList();
      // setScreenList updates context without a change reason this component tracks, so force the
      // freshly-loaded list to render.
      this.forceUpdate();
    }
  };

  componentWillUnmount(): void {
    this.unbindCanvasDismiss();
    this.clearDropHoverTimer();
  }

  // Canvas is an iframe, so its clicks never reach rc-dropdown's outside-click
  // detection. Listen there too while open (capture: the agent stops propagation).
  private canvasDoc: Document | null = null;

  private dismissFromCanvas = (): void => this.setOpen(false);

  private bindCanvasDismiss = (): void => {
    this.unbindCanvasDismiss();
    try {
      const iframe = document.getElementById("kuika_iframeAgent_canvas") as HTMLIFrameElement | null;
      const doc: Document | null = iframe?.contentDocument ?? null;
      if (!doc) return;
      doc.addEventListener("mousedown", this.dismissFromCanvas, true);
      this.canvasDoc = doc;
    } catch {
      // Canvas not mounted yet — nothing to dismiss from.
    }
  };

  private unbindCanvasDismiss = (): void => {
    this.canvasDoc?.removeEventListener("mousedown", this.dismissFromCanvas, true);
    this.canvasDoc = null;
  };

  /**
   * Recently-opened screens are tracked server-side per user+project (the same feed the
   * screens panel's "Recently Visited" section uses), so the tab reflects real history
   * rather than this tab's session.
   */
  private loadRecentScreens = async (): Promise<void> => {
    const userId: string | undefined = this.props.context?.userInfo?.id;
    const projectId: string | undefined = this.props.sd.project?.id;
    if (!userId) return;
    // Match what the panel actually lists (`isSelectableScreen`): reports in Report Designer mode,
    // screens everywhere else. The feed carries both types, and a recent row the panel would never
    // list is a row that navigates you out of the tool you are in.
    const res = await ScreenService.GetRecentlyOpenedScreens(
      userId,
      RECENT_SCREEN_COUNT,
      projectId ?? "",
      this.isReportMode
    );
    if (res?.status === 200 && Array.isArray(res.data)) {
      this.setState({ recentScreens: res.data as IRecentlyOpenedScreensDto[] });
    }
  };

  setOpen = (isOpen: boolean): void => {
    // A row's "…" menu is a nested rc-dropdown, and rc-menu items `stopPropagation()` their
    // mousedown — which robs the panel's rc-trigger of the signal it uses to recognise a click
    // in one of its own sub-popups, so it asks us to close on every row-menu click. While that
    // menu owns the interaction, its items decide whether the panel closes (Duplicate / Export /
    // Delete call setOpen(false) themselves; Rename deliberately keeps the panel open for its
    // inline editor). Escape and clicks outside the panel still close it, because rc-trigger
    // clears the nested menu first and this guard is no longer armed.
    if (!isOpen && this.state.openRowMenuKey !== null) return;
    if (isOpen) {
      this.loadScreenListIfNeeded();
      this.loadRecentScreens();
      this.bindCanvasDismiss();
    } else {
      this.unbindCanvasDismiss();
    }
    this.setState(
      {
        isOpen,
        searchText: isOpen ? this.state.searchText : "",
        // On open, ensure the root and the branch containing the current screen are expanded.
        expandedFolders: isOpen
          ? this.withCurrentScreenExpanded(this.state.expandedFolders)
          : this.state.expandedFolders,
        // Row-level UI is scoped to one opening of the menu — a hover menu or a half-typed rename
        // must not be waiting for you the next time you open the breadcrumb.
        openRowMenuKey: isOpen ? this.state.openRowMenuKey : null,
        renamingScreenId: isOpen ? this.state.renamingScreenId : "",
        renameValue: isOpen ? this.state.renameValue : ""
      },
      isOpen && !this.state.isOpen ? this.scrollToCurrentScreen : undefined
    );
  };

  /**
   * The list opens on the screen you are on, in the middle of the band. One frame after the
   * render that opened its folders, and on the ON-SCREEN copy of the row — the same double-render
   * trap `findSectionElement` documents: a query that lands on the measurement copy at x=-9998
   * scrolls a list nobody can see.
   */
  private scrollToCurrentScreen = (): void => {
    requestAnimationFrame((): void => {
      const rows: HTMLElement[] = Array.from(
        document.querySelectorAll<HTMLElement>(".kuika-subbar__screen-dropdown .kuika-picker__row--current")
      );
      const row: HTMLElement | undefined = rows.find((el: HTMLElement) => el.getBoundingClientRect().right > 0);
      const list: HTMLElement | null = row?.closest<HTMLElement>(".kuika-dropdown__menu-scrollable-content") ?? null;
      if (!row || !list) return;
      const rowTop: number = row.getBoundingClientRect().top - list.getBoundingClientRect().top + list.scrollTop;
      list.scrollTop = rowTop - list.clientHeight / 2 + row.offsetHeight / 2;
    });
  };

  private withCurrentScreenExpanded = (prev: Record<string, boolean>): Record<string, boolean> => {
    const next: Record<string, boolean> = { ...prev, [ROOT_ID]: true };
    const path: string[] = this.findScreenFolderPath(this.props.sd.project?.screenFolders ?? [], this.props.sd.sdId);
    path.forEach((folderId) => {
      next[folderId] = true;
    });
    return next;
  };

  private findScreenFolderPath = (folders: IScreenFolder[], screenId: string, acc: string[] = []): string[] => {
    for (const folder of folders) {
      if (folder.screenIds?.includes(screenId)) return [...acc, folder.id];
      if (folder.children && folder.children.length > 0) {
        const childPath: string[] = this.findScreenFolderPath(folder.children, screenId, [...acc, folder.id]);
        if (childPath.length > 0) return childPath;
      }
    }
    return [];
  };

  private expandLeftPanelIfCollapsed = (): void => {
    const { sd } = this.props;
    if (sd.isLeftPanelCollapsed[sd.dashboardState]) {
      sd.setLeftPanelCollapsed(false);
    }
  };

  /**
   * The hover controls ("+" on a folder, "…" on a screen) live inside the row, so their clicks
   * must not also toggle the folder / switch the screen. Not solved with stopPropagation on the
   * trigger: rc-dropdown's open handler sits on that same element, so swallowing the click there
   * stops the menu from opening.
   */
  private isClickFromRowActions = (e: React.MouseEvent): boolean =>
    !!(e.target as HTMLElement)?.closest?.(".kuika-picker__row-actions");

  toggleFolder = (folderId: string, e: React.MouseEvent): void => {
    if (this.isClickFromRowActions(e)) return;
    e.stopPropagation();
    this.setState((prev) => ({
      expandedFolders: { ...prev.expandedFolders, [folderId]: !this.isFolderExpanded(folderId) }
    }));
  };

  private isFolderExpanded = (folderId: string): boolean => {
    const value: boolean | undefined = this.state.expandedFolders[folderId];
    if (value !== undefined) return value;
    // Root and the type groups default to expanded — they are the menu's top level,
    // and collapsing them by default would hide the whole list behind a click each.
    return (
      folderId === ROOT_ID ||
      folderId === WORKFLOW_GROUP_ID ||
      folderId === MASTER_GROUP_ID ||
      folderId === WEB_GROUP_ID ||
      folderId.startsWith(WF_PROCESS_KEY_PREFIX)
    );
  };

  switchScreen = async (screenId: string): Promise<void> => {
    this.setOpen(false);
    if (screenId === this.props.sd.sdId) return;
    await this.props.sd.saveScreen(true);
    this.props.sd.loadScreen(screenId);
  };

  private onScreenRowClick = (screenId: string, e: React.MouseEvent): void => {
    // Not a screen switch: a click on the row's "…", or any click while the rename editor is
    // open — that click blurs the editor, and letting a switch's save race the rename's save
    // is how one of the two gets lost.
    if (this.isClickFromRowActions(e) || this.state.renamingScreenId.length > 0) return;
    this.switchScreen(screenId);
  };

  /**
   * A row's identity is its section plus the screen, not the screen alone: the same screen is
   * listed in RECENT and in its folder (and Master screens in RECENT and the Master group), and
   * a screen-id-only key made every copy's "…" menu open at once, stacking two menus on screen.
   * Folder "+" rows key off the folder id and stay outside this namespace.
   */
  private screenRowKey = (section: string, screenId: string): string => `${section}:${screenId}`;

  private isRowMenuOpen = (rowKey: string): boolean => this.state.openRowMenuKey === rowKey;

  /**
   * A mirror of which row control is open, not its source of truth — the row menus hand `setOpen`
   * to KuikaDropdownMenu without `open`, so each dropdown owns its own visibility exactly as the
   * screens panel's three-dot menus do. That ownership is required, not stylistic: rc-menu mounts
   * the dropdown body a second time in a display:none registration pass, so every row control
   * exists twice. Sharing one controlled flag between the twins made both popups open at once and
   * left a re-click unable to close — the hidden twin closed on mousedown and the visible trigger
   * reopened on click. This mirror still drives the row's hover-slot highlight and tells setOpen
   * that a row menu owns the interaction, so the panel stays open behind it.
   */
  private setRowMenuOpen = (rowKey: string, open: boolean): void =>
    this.setState((prev: ISubBarScreenSelectorState) => ({
      // Only clear on close when this row is still the owner — an adjacent row may already have
      // claimed the slot by the time a closing animation reports back.
      openRowMenuKey: open ? rowKey : prev.openRowMenuKey === rowKey ? null : prev.openRowMenuKey
    }));

  // --- Row actions (rename / duplicate / export / delete) -------------------
  // The flows themselves are shared with the screens panel; only the inline editor and the
  // confirmation modal are local. See shared/utility/screen-row-actions.tsx.

  private get designType(): DesignType {
    return this.isReportMode ? "report" : "screen";
  }

  private startRename = (screenId: string): void => {
    const screen: IScreenDesignInfo | undefined = (this.props.sd.screenList || []).find(
      (item: IScreenDesignInfo) => item.id === screenId
    );
    this.setState({ renamingScreenId: screenId, renameValue: screen?.name ?? "" }, this.focusRenameEditor);
  };

  /**
   * Focus is placed by hand rather than with `autoFocus`, and only on the input the user can
   * actually see. rc-menu renders the menu body twice (a hidden copy it measures with, parked at
   * x=-9998), so an autoFocus editor mounts twice: the second instance steals focus, which blurs
   * the first — and blur commits. The rename silently completed with the untouched name before
   * anyone could type. Same on-screen test as findSectionElement.
   */
  private focusRenameEditor = (): void => {
    const inputs: HTMLInputElement[] = Array.from(
      document.querySelectorAll<HTMLInputElement>(`[data-testid="${RENAME_INPUT_TEST_ID}"]`)
    );
    const onScreen: HTMLInputElement | undefined = inputs.find(
      (input: HTMLInputElement) => input.getBoundingClientRect().right > 0
    );
    onScreen?.focus();
    onScreen?.select();
  };

  private cancelRename = (): void => this.setState({ renamingScreenId: "", renameValue: "" });

  private commitRename = async (): Promise<void> => {
    if (!this.state.renamingScreenId || this.state.isRenameLoading) return;
    this.setState({ isRenameLoading: true });
    const outcome: ScreenRenameOutcome = await renameCurrentScreen(this.props.sd, this.state.renameValue);
    if (outcome === "rejected") {
      // Keep the editor open on the rejected value so it can be corrected in place.
      this.setState({ isRenameLoading: false });
      return;
    }
    this.setState({ renamingScreenId: "", renameValue: "", isRenameLoading: false });
    // setIsScreenWindowReload only refreshes the screens panel, which may not even be mounted —
    // refresh our own copy of the list so the breadcrumb and the tree show the new name.
    await this.props.sd.setScreenList();
    this.forceUpdate();
  };

  private onRenameKeyDown = (e: React.KeyboardEvent): void => {
    if (e.keyCode === 13) {
      this.commitRename();
    } else if (e.keyCode === 27) {
      this.cancelRename();
    }
  };

  // The screen (or report) name also selects its root, so the right panel returns to its
  // page-level settings and actions.
  private openFromTrigger = (isComponentSelected: boolean): void => {
    if (isComponentSelected) {
      this.props.sd.setSelectedDisplayItem(SpecialDIIds.rootDI.toString());
    }
    this.setOpen(true);
  };

  /**
   * Row actions that take you somewhere else close the whole breadcrumb. The row menu has to be
   * released first: while it is open, setOpen ignores close requests (see the guard there), and
   * setState batching means clearing it in the same tick would not be visible to that check yet.
   */
  private closeAfterRowAction = (onClosed?: () => void): void => {
    this.setState({ openRowMenuKey: null }, () => {
      this.setOpen(false);
      onClosed?.();
    });
  };

  private duplicateRow = (screen: IScreenDesignInfo): void => {
    this.closeAfterRowAction(() =>
      duplicateScreenFromRow(this.props.sd, screen.id, screen.name, this.props.showDomainAccessRequiredModal)
    );
  };

  private exportRow = (): void => {
    this.closeAfterRowAction(() => exportScreenKuikaFile(this.props.sd));
  };

  private requestDelete = (screen: IScreenDesignInfo): void => {
    if (!canOpenDeleteScreenModal(this.props.sd)) return;
    this.closeAfterRowAction(() =>
      this.setState({ deleteTarget: { kind: "screen", screen }, isDeleteModalOpen: true })
    );
  };

  // --- Folder row actions ----------------------------------------------------
  // Both open a modal, so both go through closeAfterRowAction — the panel has to be gone before
  // the modal appears, and the row menu released before the panel will accept a close.

  private startFolderRename = (folder: IScreenFolder): void => {
    this.closeAfterRowAction(() => this.setState({ renamingFolder: folder }));
  };

  private requestFolderDelete = (folder: IScreenFolder): void => {
    this.closeAfterRowAction(() =>
      this.setState({ deleteTarget: { kind: "folder", folder }, isDeleteModalOpen: true })
    );
  };

  /** Same split the screens panel makes: a nested folder is deleted through its parent. */
  private deleteFolder = (folder: IScreenFolder): void => {
    if (folder.parentFolderId) {
      deleteDesignerSubfolder(getSdInstance(), {
        folderType: "screen",
        parentFolderId: folder.parentFolderId,
        subFolderId: folder.id
      });
      return;
    }
    deleteDesignerFolder(getSdInstance(), { folderType: "screen", folderId: folder.id });
  };

  private get deleteTargetName(): string {
    const target: DeleteTarget | null = this.state.deleteTarget;
    if (!target) return "";
    return target.kind === "folder" ? target.folder.name : target.screen.name;
  }

  private confirmDelete = async (): Promise<void> => {
    const target: DeleteTarget | null = this.state.deleteTarget;
    if (!target) return;
    if (target.kind === "folder") {
      this.deleteFolder(target.folder);
      return;
    }
    await deleteScreenFromRow(this.props.sd, target.screen, this.designType, () => this.loadRecentScreens());
  };

  // --- Drag a screen into a folder -----------------------------------------
  // Deliberately the SAME mechanism as the screens panel: react-beautiful-dnd, folders as
  // droppables, `droppable2` as the unfoldered bucket, the whole row as the drag handle (the grip
  // is only an affordance — see KuikaMenuItem, which also spreads dragHandleProps on the row), and
  // the panel's onDragEnd / spring-loaded onDragUpdate branches. Two earlier attempts are worth not
  // repeating: HTML5 drag-and-drop opens a browser-level drag session that every native drop target
  // reacts to (the canvas lit up as if a component were about to land on it) and dies when rc-menu
  // re-measures the menu body mid-drag; a hand-rolled pointer drag avoided that but is one more
  // interaction model for the same job. RBD's usual objection here — rc-menu renders this body
  // twice, so each row exists twice — does not bite, because the DragDropContext lives INSIDE the
  // body: each rendered copy gets its own context, and ids only need to be unique within one
  // (the panel likewise runs several contexts, two of which both use `droppable2`).
  //
  // The mutation helpers enforce single-folder membership themselves (addScreenToFolder strips the
  // screen from every other folder first), so a move is one add.

  /** No handles with nothing to drag into, and reports don't get them in the panel either. */
  private get canDragScreens(): boolean {
    return !this.isReportMode && (this.props.sd.project?.screenFolders?.length ?? 0) > 0;
  }

  private dropHoverTimer: ReturnType<typeof setTimeout> | null = null;
  private dropHoverTargetId: string | null = null;

  private clearDropHoverTimer = (): void => {
    if (this.dropHoverTimer) {
      clearTimeout(this.dropHoverTimer);
      this.dropHoverTimer = null;
    }
    this.dropHoverTargetId = null;
  };

  /**
   * Spring-loaded expand, lifted from the panel: open a collapsed folder only once the drag dwells
   * over it, so passing across folders doesn't fire a setState (and a full re-render) per folder.
   */
  private onDragUpdate = (update: DragUpdate): void => {
    const destinationId: string | undefined = update?.destination?.droppableId;
    if (!destinationId || destinationId === UNFOLDERED_DROPPABLE_ID) {
      this.clearDropHoverTimer();
      return;
    }
    if (destinationId === this.dropHoverTargetId) return;
    this.clearDropHoverTimer();
    if (this.isFolderExpanded(destinationId)) return;
    this.dropHoverTargetId = destinationId;
    this.dropHoverTimer = setTimeout(() => {
      this.dropHoverTimer = null;
      this.expandFolders([destinationId]);
    }, DROP_HOVER_EXPAND_MS);
  };

  private onDragEnd = (result: DropResult): void => {
    this.clearDropHoverTimer();
    const source: DraggableLocation = result.source;
    const destination: DraggableLocation | null | undefined = result.destination;
    if (!source) return;

    // Reordering inside one list, or dropping an already-unfoldered screen outside any folder,
    // changes no membership — skip the mutation and the refresh it would cost.
    const isSameList: boolean = destination?.droppableId === source.droppableId;
    const isUnfolderedSource: boolean = source.droppableId === UNFOLDERED_DROPPABLE_ID;
    if (isSameList || (!destination && isUnfolderedSource)) return;

    const screenId: string = result.draggableId;
    const folders: IScreenFolder[] = this.props.sd.project?.screenFolders ?? [];
    const droppedOutside: boolean = !destination || destination.droppableId === UNFOLDERED_DROPPABLE_ID;
    // Parent comes from getFolderWithParent rather than folder.parentFolderId: the lookup helpers
    // return the stored folder object and don't populate that field.
    const targetId: string = droppedOutside ? source.droppableId : destination!.droppableId;
    const withParent = FolderHelper.getFolderWithParent(targetId, folders);
    const parentId: string | undefined = withParent?.parent?.id;

    if (droppedOutside) {
      if (parentId) {
        removeDesignerItemFromSubfolder(getSdInstance(), {
          folderType: "screen",
          parentFolderId: parentId,
          subFolderId: source.droppableId,
          itemId: screenId
        });
      } else {
        removeDesignerItemFromFolder(getSdInstance(), {
          folderType: "screen",
          folderId: source.droppableId,
          itemId: screenId
        });
      }
    } else if (parentId) {
      addDesignerItemToSubfolder(getSdInstance(), {
        folderType: "screen",
        parentFolderId: parentId,
        subFolderId: targetId,
        itemId: screenId
      });
      this.expandFolders([parentId, targetId]);
    } else {
      addDesignerItemToFolder(getSdInstance(), { folderType: "screen", folderId: targetId, itemId: screenId });
      this.expandFolders([targetId]);
    }

    // The helpers mutate project.screenFolders in place but only re-render once the project PATCH
    // resolves; the panel papers over that with its reload flag (which nothing consumes when the
    // panel is closed), so repaint the already-mutated tree ourselves too.
    this.props.sd.setIsScreenWindowReload(true);
    this.forceUpdate();
  };

  private expandFolders = (folderIds: (string | undefined)[]): void => {
    const ids: string[] = folderIds.filter((id): id is string => Boolean(id));
    if (ids.length === 0) return;
    this.setState((prev: ISubBarScreenSelectorState) => {
      const next: Record<string, boolean> = { ...prev.expandedFolders };
      ids.forEach((id: string) => {
        next[id] = true;
      });
      return { expandedFolders: next };
    });
  };

  /**
   * Folder the next create should land in, handed to the screens panel through the shared
   * context because the modal flags are the only channel this component has into it.
   * Null clears it, so the header "+" always creates at the project root.
   */
  private setFolderTarget = (folderId: string | null): void => {
    this.props.sd.setPendingScreenFolderTargetId(folderId);
  };

  // Both creates are reachable from a folder row's "+", which means its menu still owns the
  // interaction when the item fires — and while it does, setOpen ignores close requests. Closing
  // the plain way left the panel floating in front of the modal it had just opened, so release
  // the row menu first, exactly as the row actions do.
  openNewScreen = (): void => {
    this.closeAfterRowAction(() => {
      // A report is one name, so it is asked for here rather than by navigating to the
      // landing page — which this dropdown already offers, as "Manage all reports", and
      // which would close the report you are editing.
      if (this.isReportMode) {
        this.setState({ isNewReportModalVisible: true });
        return;
      }
      this.expandLeftPanelIfCollapsed();
      this.props.sd.setActiveLeftPanelByClick(this.targetLeftPanel);
      this.props.sd.setNewScreenModalVisibility(true);
    });
  };

  openNewFolder = (): void => {
    this.closeAfterRowAction(() => {
      this.expandLeftPanelIfCollapsed();
      this.props.sd.setActiveLeftPanelByClick(this.targetLeftPanel);
      this.props.sd.setNewFolderModalVisibility(true);
    });
  };

  manageAllScreens = (): void => {
    this.setOpen(false);
    this.expandLeftPanelIfCollapsed();
    this.props.sd.setActiveLeftPanelByClick(this.targetLeftPanel);
  };

  // Report Designer reuses this selector for its own pages, so the list flips from
  // "every page-like screen" to "reports only".
  private get isReportMode(): boolean {
    return this.props.sd.dashboardState === DashboardState.reportDesigner;
  }

  private get targetLeftPanel(): LeftPanelType {
    return this.isReportMode ? LeftPanelType.Reports : LeftPanelType.Screens;
  }

  /** Same gate the panel puts on its Workflow Screens pencil. */
  private get canManageWorkflow(): boolean {
    return KMainFunctions.hasUserAccessInWorkspaceByProjectRole(
      EUserAccessPart.ManageWorkflow,
      this.props.sd.project?.projectAccess
    );
  }

  private isSelectableScreen = (screen: IScreenDesignInfo): boolean => {
    const screenType: EScreenType | undefined = screen.publicMetaData?.screenType;
    if (this.isReportMode) {
      return screenType === EScreenType.Report;
    }
    return screenType === undefined || !NON_PAGE_SCREEN_TYPES.includes(screenType);
  };

  private isMasterScreen = (screen: IScreenDesignInfo): boolean =>
    screen.publicMetaData?.screenType === EScreenType.Master;

  /** The distinct `WebScreen` type — a mobile project's web-rendered pages, the panel's "Web Screens". */
  private isWebTypeScreen = (screen: IScreenDesignInfo): boolean =>
    screen.publicMetaData?.screenType === EScreenType.WebScreen;

  /** The panel hides its "Web Screens" accordion for Desktop projects — mirror that gate. */
  private get showsWebScreens(): boolean {
    return !this.isReportMode && this.props.sd.project?.projectType !== EProjectType.Desktop;
  }

  /**
   * Workflow membership comes from `project.processStepScreenIds` — the same source the screens
   * panel's "Workflow Screens" accordion and the shared role icon use. NOT
   * `publicMetaData.subScreenType`: nothing in the platform ever sets that field, so keying on it
   * counted every workflow screen as Web and pinned the Workflow chip to 0.
   */
  private isWorkflowScreen = (screen: IScreenDesignInfo): boolean =>
    !!this.props.sd.project?.processStepScreenIds?.some(
      (ps: IProcessStepScreensInfo): boolean => ps.screenId === screen.id
    );

  /** Managed flag of a workflow screen's step binding — colors the shared role icon like the panel. */
  private isManagedWorkflowScreen = (screenId: string): boolean =>
    !!this.props.sd.project?.processStepScreenIds?.find(
      (ps: IProcessStepScreensInfo): boolean => ps.screenId === screenId
    )?.isManaged;

  private byName = (a: IScreenDesignInfo, b: IScreenDesignInfo): number => (a.name || "").localeCompare(b.name || "");

  /**
   * The folder tree lists the plain screens (`EScreenType.Default`, the panel's "Screens"
   * accordion): every other kind lives in its own group — workflow screens by step binding,
   * web screens (`EScreenType.WebScreen`) and masters by type. One screen, one section, so the
   * chips' counts always add up to All. Report mode keeps its single flat report list.
   */
  private getAllSelectableScreens = (): IScreenDesignInfo[] => {
    const listable: IScreenDesignInfo[] = (this.props.sd.screenList || []).filter(this.isSelectableScreen);
    if (this.isReportMode) return listable;
    return listable.filter((screen) => !this.isWorkflowScreen(screen) && !this.isWebTypeScreen(screen));
  };

  private getWorkflowScreens = (): IScreenDesignInfo[] =>
    (this.props.sd.screenList || []).filter(this.isWorkflowScreen).sort(this.byName);

  private getMasterScreens = (): IScreenDesignInfo[] =>
    (this.props.sd.screenList || []).filter(this.isMasterScreen).sort(this.byName);

  private getWebScreens = (): IScreenDesignInfo[] =>
    (this.props.sd.screenList || []).filter(this.isWebTypeScreen).sort(this.byName);

  /**
   * Workflow screens sliced per process, named and sorted like the screens panel's per-workflow
   * collapsibles. A screen whose binding points at a process no longer in `workFlowList` is
   * dropped here (the panel does the same) — a stale binding has no group to live under.
   */
  private getWorkflowProcessGroups = (screens: IScreenDesignInfo[]): IWorkflowProcessGroup[] => {
    const byProcess: Map<string, IScreenDesignInfo[]> = new Map();
    for (const screen of screens) {
      const processId: string | undefined = this.props.sd.project?.processStepScreenIds?.find(
        (ps: IProcessStepScreensInfo): boolean => ps.screenId === screen.id
      )?.processId;
      if (!processId) continue;
      const group: IScreenDesignInfo[] = byProcess.get(processId) ?? [];
      group.push(screen);
      byProcess.set(processId, group);
    }
    return Array.from(byProcess.entries())
      .map(([processId, group]): IWorkflowProcessGroup | null => {
        const workflow = this.props.sd.workFlowList.find((wf): boolean => wf.id === processId);
        if (!workflow) return null;
        return { processId, name: workflow.displayName || workflow.name || "", screens: group.sort(this.byName) };
      })
      .filter((group): group is IWorkflowProcessGroup => group !== null)
      .sort((a: IWorkflowProcessGroup, b: IWorkflowProcessGroup): number =>
        a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: "base" })
      );
  };

  /**
   * Recent comes back as a separate DTO (id + name only), so it is resolved against the
   * screen list to reuse the same row rendering — and to drop entries for screens that
   * have since been deleted. The CURRENT screen is excluded (it owns the card on top),
   * leaving the last {@link RECENT_VISIBLE_COUNT} screens you were on before this one.
   */
  private getRecentScreens = (): IScreenDesignInfo[] => {
    const byId: Map<string, IScreenDesignInfo> = new Map(
      (this.props.sd.screenList || []).map((screen) => [screen.id, screen])
    );
    return this.state.recentScreens
      .map((entry: IRecentlyOpenedScreensDto) => byId.get(entry.screenId))
      .filter((screen): screen is IScreenDesignInfo => screen !== undefined)
      .filter((screen: IScreenDesignInfo): boolean => !this.isCurrentScreen(screen.id))
      .slice(0, RECENT_VISIBLE_COUNT);
  };

  private getTabCount = (tab: ScreenFilterTab): number => {
    if (tab === "web") return this.getWebScreens().length;
    if (tab === "workflow") return this.getWorkflowScreens().length;
    if (tab === "master") return this.getMasterScreens().length;
    return (
      this.getAllSelectableScreens().length +
      this.getWebScreens().length +
      this.getWorkflowScreens().length +
      this.getMasterScreens().length
    );
  };

  /**
   * Search spans every switchable page — plain, web AND workflow screens — not just the folder
   * tree's slice: a screen you can jump to must be findable no matter which section lists it.
   * (Masters/components/reports stay out; they aren't page targets here.)
   */
  private getSearchMatches = (): IScreenDesignInfo[] => {
    const search: string = KMainFunctions.turkishCharsToEnglish(this.state.searchText || "").toLowerCase();
    return (this.props.sd.screenList || [])
      .filter(this.isSelectableScreen)
      .filter((screen) => {
        const name: string = KMainFunctions.turkishCharsToEnglish(screen.name || "").toLowerCase();
        return name.includes(search);
      })
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  private getFolderScreens = (folder: IScreenFolder): IScreenDesignInfo[] => {
    const ids: string[] = folder.screenIds ?? [];
    return this.getAllSelectableScreens()
      .filter((screen) => ids.includes(screen.id))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  private getOrphanScreens = (): IScreenDesignInfo[] => {
    const folders: IScreenFolder[] = this.props.sd.project?.screenFolders ?? [];
    return this.getAllSelectableScreens()
      .filter((screen) => !this.props.sd.findFolderByScreenId(screen.id, folders))
      .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  private countFolders = (folders: IScreenFolder[]): number => {
    return folders.reduce((acc, folder) => acc + 1 + this.countFolders(folder.children ?? []), 0);
  };

  private isCurrentScreen = (screenId: string): boolean => KMainFunctions.getSdIdFromUrl() === screenId;

  /**
   * Name of the screen actually being edited. Resolved from the URL against the screen
   * list rather than read off sd.sdName: that field is only refreshed by setScreenList(),
   * which a screen switch doesn't always reach, so it can keep naming the previous screen
   * after you pick another one from the screens panel. The URL is the same source the
   * tree's CURRENT row uses, so the two can't disagree.
   */
  /**
   * Last name this resolved to. Deleting the screen you are on moves the URL to its replacement
   * before the list and `sdName` catch up, so for a beat neither source can name the screen — and
   * a delete started from this panel repaints it in exactly that beat (closing the row menu and
   * the modal are local setStates). Falling through to a placeholder there put "Untitled" in the
   * breadcrumb, where it stuck until something else happened to re-render us. Showing the name we
   * had a moment ago is both truer and quieter than a placeholder.
   */
  private lastResolvedScreenName: string = "";

  private getCurrentScreenName = (): string => {
    const urlScreenId: string = KMainFunctions.getSdIdFromUrl();
    const fromList: IScreenDesignInfo | undefined = (this.props.sd.screenList || []).find(
      (screen: IScreenDesignInfo) => screen.id === urlScreenId
    );
    const resolved: string = fromList?.name || this.props.sd.sdName || "";
    if (resolved) {
      this.lastResolvedScreenName = resolved;
    }
    return resolved || this.lastResolvedScreenName || "Untitled";
  };

  /**
   * The New Screen / New Folder menu behind a "+". `folderId` decides where the create
   * lands: null for the header's "+" (project root), a folder id for a folder row's.
   * `rowKey` is set for the in-row "+"s so the trigger survives the pointer leaving the row.
   * Notify-only, never `open`: see setRowMenuOpen.
   */
  private renderAddMenu = (
    folderId: string | null,
    testId: string,
    className: string,
    rowKey?: string
  ): React.ReactNode => (
    <KuikaDropdownMenu
      setOpen={rowKey ? (open: boolean) => this.setRowMenuOpen(rowKey, open) : undefined}
      // The tree scrolls under this popup, so follow the row out of view by dismissing.
      closeOnScroll
      options={[
        {
          key: "new-screen",
          label: "New Screen",
          icon: <KuikaSvg name="file-plus" type="sidebar-icons" width={16} height={16} />,
          group: 1,
          onClick: () => {
            this.setFolderTarget(folderId);
            this.openNewScreen();
          }
        },
        {
          key: "new-folder",
          label: "New Folder",
          icon: <KuikaSvg name="folder-plus" type="sidebar-icons" width={16} height={16} />,
          group: 1,
          onClick: () => {
            this.setFolderTarget(folderId);
            this.openNewFolder();
          }
        }
      ]}
      overlayClassName="kuika-subbar__add-dropdown"
      placement="bottomRight"
    >
      {/* No stopPropagation here — rc-dropdown's open handler lives on this same element,
          so swallowing the click stopped the menu from opening. The row ignores clicks
          coming from inside instead (see isClickFromRowActions). */}
      <div className={className} data-testid={testId} role="button" tabIndex={0}>
        <KuikaSvg name="plus" width={16} height={16} pointer />
      </div>
    </KuikaDropdownMenu>
  );

  /**
   * Hover slot at the right end of a row. One slot class for both controls, so a single pair of
   * CSS rules governs "hidden at rest / shown on hover" and the count it replaces on a folder row.
   */
  private renderRowActions = (children: React.ReactNode): React.ReactNode => (
    <span className="kuika-picker__row-actions">{children}</span>
  );

  /** The project root's "+" — creates at project level, the panel's "Screens +" equivalent. */
  private renderFolderAdd = (folderId: string | null, testId: string, rowKey: string): React.ReactNode =>
    this.renderRowActions(this.renderAddMenu(folderId, testId, "kuika-picker__row-menu", rowKey));

  /**
   * The folder row's "…" — the same four actions the screens panel puts behind a folder's
   * three-dot, in the same order. It replaces the row's "+": creating inside the folder is one of
   * these items, so a separate control would be a second way to say the same thing.
   */
  private renderFolderRowMenu = (folder: IScreenFolder): React.ReactNode => {
    const options: KuikaDropdownMenuItemType[] = [
      {
        key: "new-screen",
        label: "New Screen",
        icon: <KuikaSvg name="file-plus" type="sidebar-icons" width={16} height={16} />,
        onClick: () => {
          this.setFolderTarget(folder.id);
          this.openNewScreen();
        }
      },
      {
        key: "add-sub-folder",
        label: "Add sub-folder",
        icon: <KuikaSvg name="folder-plus" type="sidebar-icons" width={16} height={16} />,
        // One level of nesting, the same limit the screens panel enforces.
        hidden: !!folder.parentFolderId,
        onClick: () => {
          this.setFolderTarget(folder.id);
          this.openNewFolder();
        }
      },
      {
        key: "rename",
        label: "Rename",
        icon: <KuikaSvg id={`folderRenameIcon-${folder.id}`} width={16} height={16} name="edit" />,
        onClick: () => this.startFolderRename(folder)
      },
      {
        key: "delete",
        label: "Delete",
        icon: <KuikaSvg id={`folderDeleteIcon-${folder.id}`} width={16} height={16} name="trash" />,
        onClick: () => this.requestFolderDelete(folder),
        danger: true
      }
    ];
    return this.renderRowActions(
      <KuikaDropdownMenu
        options={options}
        overlayClassName="kuika-subbar__row-dropdown"
        placement="bottomRight"
        setOpen={(open: boolean) => this.setRowMenuOpen(folder.id, open)}
        // The tree scrolls under this popup, so follow the row out of view by dismissing.
        closeOnScroll
      >
        <div
          className="kuika-picker__row-menu"
          data-testid={`subBarFolderMenu-${folder.id}`}
          role="button"
          tabIndex={0}
        >
          <KuikaSvg name="dots" width={16} height={16} pointer />
        </div>
      </KuikaDropdownMenu>
    );
  };

  /** A single icon button in a group header's hover slot — the type groups' equivalent of a "+". */
  private renderGroupAction = (
    testId: string,
    iconName: string,
    onClick: () => void,
    tooltip: string
  ): React.ReactNode =>
    this.renderRowActions(
      <KuikaTooltip content={tooltip}>
        <div
          className="kuika-picker__row-menu"
          data-testid={testId}
          role="button"
          tabIndex={0}
          onClick={(e) => {
            // The row toggles the group; this control must not do both.
            e.stopPropagation();
            onClick();
          }}
        >
          <KuikaSvg name={iconName} width={16} height={16} pointer />
        </div>
      </KuikaTooltip>
    );

  /**
   * Master screens are created through the same modal as any screen, but it has to be told the type.
   * The panel reads that from its own state, so ask for it over the shared channel instead — the
   * same mechanism the per-folder "+" uses for its folder target.
   */
  private openNewMasterScreen = (): void => this.openNewTypedScreen(EScreenType.Master);

  private openNewWebScreen = (): void => this.openNewTypedScreen(EScreenType.WebScreen);

  private openNewTypedScreen = (screenType: EScreenType): void => {
    this.setOpen(false);
    this.expandLeftPanelIfCollapsed();
    this.props.sd.setActiveLeftPanelByClick(this.targetLeftPanel);
    this.setFolderTarget(null);
    this.props.sd.setPendingNewScreenType(screenType);
    this.props.sd.setNewScreenModalVisibility(true);
  };

  /**
   * Workflow screens aren't hand-created — the pencil mirrors the screens panel's "manage" action.
   * Management lives on the process editor's Forms tab now (the legacy step-screen wizard modal is
   * retired from the screen lists), so this navigates to Process Automation and opens Forms.
   */
  private goToWorkflowForms = (): void => {
    this.setOpen(false);
    void redirectToProcessForms(this.props.sd);
  };

  /**
   * The screen row's "…" menu — the same actions, icons and test ids as the screens panel's
   * three-dot menu, built by the shared module.
   */
  private renderScreenRowMenu = (screen: IScreenDesignInfo, rowKey: string): React.ReactNode => {
    if (!hasAnyScreenRowAction(this.props.sd)) return null;
    const options: KuikaDropdownMenuItemType[] = buildScreenActionMenu({
      sd: this.props.sd,
      screen,
      isCurrentScreen: this.isCurrentScreen(screen.id),
      username: this.props.context?.userInfo?.username,
      handlers: {
        onRename: (screenId: string) => this.startRename(screenId),
        onDuplicate: (target: IScreenDesignInfo) => this.duplicateRow(target),
        onExport: () => this.exportRow(),
        onDelete: (target: IScreenDesignInfo) => this.requestDelete(target)
      }
    });
    if (options.length === 0) return null;
    return this.renderRowActions(
      <KuikaDropdownMenu
        options={options}
        overlayClassName="kuika-subbar__row-dropdown"
        placement="bottomRight"
        setOpen={(open: boolean) => this.setRowMenuOpen(rowKey, open)}
        // The tree scrolls under this popup, so follow the row out of view by dismissing.
        closeOnScroll
      >
        <div
          className="kuika-picker__row-menu"
          data-testid={`subBarScreenMenu-${screen.id}`}
          role="button"
          tabIndex={0}
        >
          <KuikaSvg name="dots" width={16} height={16} pointer />
        </div>
      </KuikaDropdownMenu>
    );
  };

  // Folders are shared project-wide but only the Screens panel creates them — the Reports
  // panel offers a single "new report" button. Mirror that: a plain "+" in report mode
  // instead of a one-item menu.
  private renderAddControl = (): React.ReactNode => {
    if (this.isReportMode) {
      return (
        <button
          type="button"
          className="kuika-picker__add"
          data-testid="subBarNewReport"
          aria-label="New report"
          onClick={this.openNewScreen}
        >
          <KuikaSvg name="plus" width={16} height={16} pointer />
        </button>
      );
    }
    return this.renderAddMenu(null, "subBarNewScreen", "kuika-picker__add");
  };

  private renderMenuHeader = (): React.ReactNode => (
    <div className="kuika-subbar__screen-menu-header">
      {/* Stop key/click events from reaching rc-menu so typing in search doesn't trigger menu nav.
          The create control rides beside the search: the list is where you look for a screen,
          and where one is missing is where you make it. The card that used to name the current
          screen above this is gone — the tree marks it CURRENT, and the list opens scrolled to it. */}
      <div
        className={`kuika-picker__search${this.isReportMode ? " kuika-picker__search--divided" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <KuikaInput
          size="md"
          data-testid="subBarScreenSearch"
          leftIcon={<KuikaSvg name="search" width={16} height={16} />}
          placeholder={this.isReportMode ? "Search reports and folders..." : "Search screens and folders..."}
          value={this.state.searchText}
          onChange={(_e, value) => this.setState({ searchText: String(value ?? "") })}
        />
        {this.renderAddControl()}
      </div>
      {/* Section chips belong to the sticky header, not the scroll body: they are how you
          jump between sections, so they have to stay reachable after a jump. */}
      {!this.isReportMode && this.renderTabs()}
    </div>
  );

  /**
   * Inline rename editor. Clicks and keys are stopped here so they never reach rc-menu's keyboard
   * navigation or the enclosing row's click handler.
   */
  private renderRenameEditor = (): React.ReactNode => (
    <span
      className="kuika-subbar__screen-rename"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      <KuikaInput
        size="md"
        data-testid={RENAME_INPUT_TEST_ID}
        disabled={this.state.isRenameLoading}
        value={this.state.renameValue}
        onChange={(_e, value) => this.setState({ renameValue: String(value ?? "") })}
        onKeyDown={this.onRenameKeyDown}
        onBlur={this.commitRename}
      />
    </span>
  );

  /**
   * The whole row is the drag handle — `dragHandleProps` goes on the row, exactly as
   * `KuikaMenuItem` does it in the screens panel. The grip is a hover affordance only, and like the
   * panel's it is absolutely positioned in the tree's left gutter, so it sits in the same column on
   * every row regardless of how deep that row is nested.
   */
  // ReactElement, not ReactNode: Draggable's render prop is typed to require an element.
  private renderScreenRow = (
    screen: IScreenDesignInfo,
    depth: number,
    section: string,
    provided?: DraggableProvided,
    snapshot?: DraggableStateSnapshot
  ): React.ReactElement => {
    const isCurrent: boolean = this.isCurrentScreen(screen.id);
    const rowKey: string = this.screenRowKey(section, screen.id);
    // Rename edits the row it was asked from, in place. RECENT never lists the current screen
    // (the only one Rename is offered on), so the editor cannot appear twice.
    const isRenaming: boolean = this.state.renamingScreenId === screen.id && section !== RECENT_SECTION_ID;
    const isMenuOpen: boolean = this.isRowMenuOpen(rowKey);
    const canDrag: boolean = !!provided;
    return (
      <div
        key={screen.id}
        role="button"
        tabIndex={0}
        ref={provided?.innerRef}
        {...provided?.draggableProps}
        {...provided?.dragHandleProps}
        className={`kuika-picker__row kuika-picker__row--item ${OVERFLOW_REVEAL_HOST_CLASS}${isCurrent ? " kuika-picker__row--current" : ""}${isMenuOpen ? " kuika-picker__row--menu-open" : ""}${snapshot?.isDragging ? " kuika-subbar__tree-row--dragging" : ""}`}
        // Merged, not appended: draggableProps carries the transform RBD moves the row with.
        style={{
          ...(provided?.draggableProps.style ?? {}),
          paddingLeft: `${TREE_BASE_PADDING_PX + depth * TREE_INDENT_PX + TREE_LEAF_SPACER_PX}px`,
          // Skip RBD's drop animation (its documented escape hatch) so the row lands in its real
          // DOM position instead of flying to a computed one. RBD measures every droppable ONCE, on
          // lift, and takes the landing offset from the destination's placeholder — but a collapsed
          // folder's list is display:none, so that placeholder measures 0×0 and the row was
          // animating up out of the panel (y 315 → 20) before snapping into place. Measured, not
          // guessed. Expanding the folder mid-drag can't fix it either: RBD never re-measures.
          ...(snapshot?.isDropAnimating ? { transitionDuration: "0.001s" } : {})
        }}
        data-testid={`subBarScreen-${screen.id}`}
        onClick={(e) => this.onScreenRowClick(screen.id, e)}
      >
        {canDrag && (
          <span className="kuika-subbar__tree-drag">
            <KuikaSvg name="grip-vertical" width={16} height={16} />
          </span>
        )}
        {getScreenRoleIcon(this.props.sd, screen.id, this.isManagedWorkflowScreen(screen.id))}
        {isRenaming ? (
          this.renderRenameEditor()
        ) : (
          <>
            <span className="kuika-picker__name">
              <KuikaOverflowText>{screen.name}</KuikaOverflowText>
            </span>
            {/* CURRENT sits at the right end of the row and is pushed inwards by the "…" on hover. */}
            {isCurrent && <span className="kuika-picker__current-badge">CURRENT</span>}
            {this.renderScreenRowMenu(screen, rowKey)}
          </>
        )}
      </div>
    );
  };

  /**
   * `dragIndex` is passed only by the folder tree, where rows sit inside a Droppable. The RECENT
   * band, the type groups and the search results render the same row without a Draggable — a
   * Draggable outside a Droppable throws, and those lists have no folder to reorder against.
   */
  private renderTreeScreen = (
    screen: IScreenDesignInfo,
    depth: number,
    section: string,
    dragIndex?: number
  ): React.ReactNode => {
    if (dragIndex === undefined || !this.canDragScreens) return this.renderScreenRow(screen, depth, section);
    return (
      <Draggable key={screen.id} draggableId={screen.id} index={dragIndex}>
        {(provided: DraggableProvided, snapshot: DraggableStateSnapshot) =>
          this.renderScreenRow(screen, depth, section, provided, snapshot)
        }
      </Draggable>
    );
  };

  private renderTreeFolder = (folder: IScreenFolder, depth: number): React.ReactNode => {
    const expanded: boolean = this.isFolderExpanded(folder.id);
    // Count what the folder actually lists, not every screen id it holds — otherwise the
    // badge promises rows the filter drops (most visibly in report mode, where everything
    // that isn't a report is filtered out).
    const count: number = this.getFolderScreens(folder).length;
    const isMenuOpen: boolean = this.isRowMenuOpen(folder.id);
    const folderScreens: IScreenDesignInfo[] = this.getFolderScreens(folder);
    // The droppable wraps the header too, not just the contents — that is what lets you drop onto
    // a COLLAPSED folder (which renders no contents at all) and what onDragUpdate springs open.
    return (
      <Droppable droppableId={folder.id} key={folder.id}>
        {(dropProvided: DroppableProvided, dropSnapshot: DroppableStateSnapshot) => (
          <div
            className={`kuika-subbar__tree-node${dropSnapshot.isDraggingOver ? " kuika-subbar__tree-node--drag-over" : ""}`}
            ref={dropProvided.innerRef}
            {...dropProvided.droppableProps}
          >
            <div
              className={`kuika-picker__row kuika-picker__row--folder ${OVERFLOW_REVEAL_HOST_CLASS}${isMenuOpen ? " kuika-picker__row--menu-open" : ""}`}
              style={{ paddingLeft: `${TREE_BASE_PADDING_PX + depth * TREE_INDENT_PX}px` }}
              role="button"
              tabIndex={0}
              onClick={(e) => this.toggleFolder(folder.id, e)}
            >
              <KuikaSvg name={expanded ? "chevron-down" : "chevron-right"} type="arrows" width={12} height={12} />
              <KuikaSvg name={expanded ? "folder-open" : "folder"} type="sidebar-icons" width={16} height={16} />
              <span className="kuika-picker__name">
                <KuikaOverflowText>{folder.name}</KuikaOverflowText>
              </span>
              {/* The count owns the right end at rest; on hover the "…" takes its place. */}
              <span className="kuika-picker__count">{count}</span>
              {!this.isReportMode && this.renderFolderRowMenu(folder)}
            </div>
            {/* Rendered whether or not the folder is open — the panel's KuikaCollapsePanel
                `forceRender` equivalent. Collapsing with CSS instead of unmounting is what keeps
                the set of Draggables stable: mounting rows mid-drag (which the spring-loaded
                expand below does) is unsupported by RBD and aborts the drag. */}
            <div className={`kuika-subbar__tree-children${expanded ? "" : " kuika-subbar__tree-children--collapsed"}`}>
              {(folder.children ?? []).map((child) => this.renderTreeFolder(child, depth + 1))}
              {folderScreens.map((screen, index) => this.renderTreeScreen(screen, depth + 1, TREE_SECTION_ID, index))}
              {/* Inside the list the rows actually live in, NOT after the node — the same correction
                  the panel documents on `withoutPlaceholder`. RBD reserves the drop slot here and
                  reads the drop animation's landing offset from it, so a placeholder parked outside
                  the list leaves the folder unable to grow and the dragged row aiming at the wrong
                  spot: it lands, springs back out, and only then settles. */}
              {dropProvided.placeholder}
            </div>
          </div>
        )}
      </Droppable>
    );
  };

  private renderTree = (): React.ReactNode => {
    const { sd } = this.props;
    const topFolders: IScreenFolder[] = sd.project?.screenFolders ?? [];
    const orphanScreens: IScreenDesignInfo[] = this.getOrphanScreens();
    const rootExpanded: boolean = this.isFolderExpanded(ROOT_ID);
    const rootName: string = sd.project?.name || "Screens";
    const totalScreens: number = this.getAllSelectableScreens().length;
    const isMenuOpen: boolean = this.isRowMenuOpen(ROOT_ID);
    return (
      // Same drag contract as the screens panel: one context per list, folders are droppables and
      // "droppable2" is the unfoldered bucket, so dropping there takes a screen out of its folder.
      <DragDropContext onDragEnd={this.onDragEnd} onDragUpdate={this.onDragUpdate}>
        <div className="kuika-subbar__tree-node">
          <div
            className={`kuika-picker__row kuika-picker__row--folder kuika-subbar__tree-root ${OVERFLOW_REVEAL_HOST_CLASS}${isMenuOpen ? " kuika-picker__row--menu-open" : ""}`}
            style={{ paddingLeft: `${TREE_BASE_PADDING_PX}px` }}
            role="button"
            tabIndex={0}
            onClick={(e) => this.toggleFolder(ROOT_ID, e)}
          >
            <KuikaSvg name={rootExpanded ? "chevron-down" : "chevron-right"} type="arrows" width={12} height={12} />
            <KuikaSvg name={rootExpanded ? "folder-open" : "folder"} type="sidebar-icons" width={16} height={16} />
            <span className="kuika-picker__name">
              <KuikaOverflowText>{rootName}</KuikaOverflowText>
            </span>
            <span className="kuika-picker__count">{totalScreens}</span>
            {/* Root's "+" creates at project level — null target, same as the header's. */}
            {!this.isReportMode && this.renderFolderAdd(null, "subBarRootAdd", ROOT_ID)}
          </div>
          <div
            className={`kuika-subbar__tree-children${rootExpanded ? "" : " kuika-subbar__tree-children--collapsed"}`}
          >
            {topFolders.map((folder) => this.renderTreeFolder(folder, 1))}
            <Droppable droppableId={UNFOLDERED_DROPPABLE_ID}>
              {(dropProvided: DroppableProvided) => (
                <div ref={dropProvided.innerRef} {...dropProvided.droppableProps}>
                  {orphanScreens.map((screen, index) => this.renderTreeScreen(screen, 1, TREE_SECTION_ID, index))}
                  {dropProvided.placeholder}
                </div>
              )}
            </Droppable>
          </div>
        </div>
      </DragDropContext>
    );
  };

  /**
   * Jump to a section: expand it (so the chip never lands you on a collapsed header) and
   * scroll it to the top of the list. "All" goes back to the very top.
   */
  private goToSection = (tab: ScreenFilterTab): void => {
    const expand: Record<string, boolean> = {};
    if (tab === "screens" || tab === "all") expand[ROOT_ID] = true;
    if (tab === "web") expand[WEB_GROUP_ID] = true;
    if (tab === "workflow") expand[WORKFLOW_GROUP_ID] = true;
    if (tab === "master") expand[MASTER_GROUP_ID] = true;

    this.setState(
      (prev: ISubBarScreenSelectorState) => ({
        activeTab: tab,
        expandedFolders: { ...prev.expandedFolders, ...expand }
      }),
      () => {
        // "All" means the whole list, so it returns to the very top rather than to a section.
        if (tab === "all") {
          const list: HTMLElement | null =
            this.findSectionElement("screens")?.closest<HTMLElement>(".kuika-dropdown__menu-scrollable-content") ??
            null;
          list?.scrollTo({ top: 0, behavior: "smooth" });
          return;
        }
        this.findSectionElement(tab)?.scrollIntoView({ block: "start", behavior: "smooth" });
      }
    );
  };

  /**
   * Section anchors above the body. Not KuikaTabs: that primitive is a segmented control
   * with a sliding thumb, while these are count-bearing chips sitting directly on the menu.
   */
  private renderTabs = (): React.ReactNode => (
    <div className="kuika-picker__chips" onClick={(e) => e.stopPropagation()}>
      {SCREEN_FILTER_TABS.filter(
        // Same gate as the panel's "Web Screens" accordion: Desktop projects have no web slice.
        (tab: IScreenFilterTabDef): boolean => tab.key !== "web" || this.showsWebScreens
      ).map((tab: IScreenFilterTabDef) => {
        const isActive: boolean = this.state.activeTab === tab.key;
        const count: number = this.getTabCount(tab.key);
        return (
          <button
            key={tab.key}
            type="button"
            data-testid={`subBarScreenTab-${tab.key}`}
            className={`kuika-picker__chip${isActive ? " kuika-picker__chip--active" : ""}`}
            onClick={() => this.goToSection(tab.key)}
          >
            <span>{tab.label}</span>
            {/* An empty section's chip stays as a plain anchor — a framed "0" is noise, and the
                boxes it saves are what keep five chips breathing in the 319px row. */}
            {count > 0 && <span className="kuika-picker__chip-count">{count}</span>}
          </button>
        );
      })}
    </div>
  );

  /**
   * A non-foldered type group (Web / Workflow / Master) with its own count and empty text.
   * `childrenOverride` swaps the flat screen rows for custom content (the Workflow group's
   * per-process sub-nodes) while keeping the header row — count, action, expand — identical.
   */
  private renderGroup = (
    key: string,
    title: string,
    screens: IScreenDesignInfo[],
    emptyText: string,
    showCount: boolean = true,
    action?: React.ReactNode,
    childrenOverride?: React.ReactNode
  ): React.ReactNode => {
    const expanded: boolean = this.isFolderExpanded(key);
    return (
      <div key={key} className="kuika-subbar__tree-node">
        <div
          className={`kuika-picker__row kuika-picker__row--folder ${OVERFLOW_REVEAL_HOST_CLASS}`}
          style={{ paddingLeft: `${TREE_BASE_PADDING_PX}px` }}
          role="button"
          tabIndex={0}
          onClick={(e) => this.toggleFolder(key, e)}
        >
          <KuikaSvg name={expanded ? "chevron-down" : "chevron-right"} type="arrows" width={12} height={12} />
          <KuikaSvg name={expanded ? "folder-open" : "folder"} type="sidebar-icons" width={16} height={16} />
          <span className="kuika-picker__name">
            <KuikaOverflowText>{title}</KuikaOverflowText>
          </span>
          {showCount && <span className="kuika-picker__count">{screens.length}</span>}
          {action}
        </div>
        {expanded && (
          <div className="kuika-subbar__tree-children">
            {screens.length === 0 && <div className="kuika-picker__empty">{emptyText}</div>}
            {screens.length > 0 && (childrenOverride ?? screens.map((screen) => this.renderTreeScreen(screen, 1, key)))}
          </div>
        )}
      </div>
    );
  };

  /**
   * One process's collapsible sub-node inside the Workflow group — the same per-workflow grouping
   * the screens panel shows, at one tree level down (workflow icon + process name + count).
   */
  private renderWorkflowProcessGroup = (group: IWorkflowProcessGroup): React.ReactNode => {
    const key: string = `${WF_PROCESS_KEY_PREFIX}${group.processId}`;
    const expanded: boolean = this.isFolderExpanded(key);
    return (
      <div key={key} className="kuika-subbar__tree-node">
        <div
          className={`kuika-picker__row kuika-picker__row--folder ${OVERFLOW_REVEAL_HOST_CLASS}`}
          style={{ paddingLeft: `${TREE_BASE_PADDING_PX + TREE_INDENT_PX}px` }}
          role="button"
          tabIndex={0}
          data-testid={`subBarWorkflowProcess-${group.processId}`}
          onClick={(e) => this.toggleFolder(key, e)}
        >
          <KuikaSvg name={expanded ? "chevron-down" : "chevron-right"} type="arrows" width={12} height={12} />
          <KuikaSvg name="workflow-managed" type="workflow-icons" width={16} height={16} />
          <span className="kuika-picker__name">
            <KuikaOverflowText>{group.name}</KuikaOverflowText>
          </span>
          <span className="kuika-picker__count">{group.screens.length}</span>
        </div>
        {expanded && (
          <div className="kuika-subbar__tree-children">
            {group.screens.map((screen) => this.renderTreeScreen(screen, 2, key))}
          </div>
        )}
      </div>
    );
  };

  /**
   * The Workflow group's body: per-process sub-nodes when the project runs more than one process
   * (the panel's grouping gate is `workFlowList.length > 1`), a flat list otherwise.
   */
  private renderWorkflowChildren = (screens: IScreenDesignInfo[]): React.ReactNode | undefined => {
    if (screens.length === 0 || this.props.sd.workFlowList.length <= 1) return undefined;
    return this.getWorkflowProcessGroups(screens).map((group) => this.renderWorkflowProcessGroup(group));
  };

  /**
   * The whole tree, always. The type chips above are anchors, not filters (per Figma:
   * with "Web" selected the tree still reads "Project Apps 23" and every folder is
   * present) — clicking one expands that section and scrolls to it, so you never lose
   * sight of where the rest of the project is.
   */
  private renderTabContent = (): React.ReactNode => {
    const recents: IScreenDesignInfo[] = this.getRecentScreens();
    const workflowScreens: IScreenDesignInfo[] = this.getWorkflowScreens();
    return (
      <>
        {recents.length > 0 && (
          <div>
            <span className="kuika-picker__section-label">RECENT</span>
            <div className="kuika-subbar__tree-children">
              {recents.map((s) => this.renderTreeScreen(s, 0, RECENT_SECTION_ID))}
            </div>
          </div>
        )}
        {/* Divider between the top-level sections: without it the tree root lines up with the
            RECENT rows and the whole list reads as one long RECENT. */}
        <div data-subbar-section="screens" className={recents.length > 0 ? "kuika-picker__section" : undefined}>
          {this.renderTree()}
        </div>
        {this.showsWebScreens && (
          <div data-subbar-section="web" className="kuika-picker__section">
            {this.renderGroup(
              WEB_GROUP_ID,
              "Web Screens",
              this.getWebScreens(),
              "No Web Screens Yet",
              true,
              getScreenActionAccess(this.props.sd).canCreateScreen
                ? this.renderGroupAction("subBarNewWebScreen", "plus", this.openNewWebScreen, "New web screen")
                : undefined
            )}
          </div>
        )}
        <div data-subbar-section="workflow" className="kuika-picker__section">
          {this.renderGroup(
            WORKFLOW_GROUP_ID,
            "Workflow",
            workflowScreens,
            "No Workflow Screens Yet",
            true,
            this.canManageWorkflow
              ? this.renderGroupAction(
                  "subBarWorkflowScreensEdit",
                  "edit",
                  this.goToWorkflowForms,
                  "Manage workflow screens"
                )
              : undefined,
            this.renderWorkflowChildren(workflowScreens)
          )}
        </div>
        <div data-subbar-section="master" className="kuika-picker__section">
          {this.renderGroup(
            MASTER_GROUP_ID,
            "Master",
            this.getMasterScreens(),
            "No Master Screens Yet",
            true,
            // Same access gate the panel's master "+" uses (getMasterScreensDropdown).
            getScreenActionAccess(this.props.sd).canCreateScreen
              ? this.renderGroupAction("subBarNewMasterScreen", "plus", this.openNewMasterScreen, "New master screen")
              : undefined
          )}
        </div>
      </>
    );
  };

  private renderBody = (): React.ReactNode => {
    const searching: boolean = (this.state.searchText || "").trim().length > 0;
    const matches: IScreenDesignInfo[] = searching ? this.getSearchMatches() : [];
    if (searching) {
      return (
        <div className="kuika-subbar__screen-body">
          {matches.length > 0 ? (
            <div className="kuika-subbar__tree-children">
              {matches.map((screen) => this.renderTreeScreen(screen, 0, SEARCH_SECTION_ID))}
            </div>
          ) : (
            <div className="kuika-picker__empty">No screens found</div>
          )}
        </div>
      );
    }
    // Report Designer lists reports only — the type tabs have nothing to slice there.
    if (this.isReportMode) {
      return (
        <div className="kuika-subbar__screen-body">
          <span className="kuika-picker__section-label">ALL REPORTS</span>
          {this.renderTree()}
        </div>
      );
    }
    // The chips are NOT rendered here — they live in the sticky header, otherwise scrolling
    // to a section carries them out of view and you can't jump anywhere else.
    return <div className="kuika-subbar__screen-body">{this.renderTabContent()}</div>;
  };

  private renderFooter = (): React.ReactNode => {
    // The full project count (web + workflow + master), matching the "All" chip — the web-only
    // count read as a contradiction next to it.
    const screenCount: number = this.isReportMode ? this.getAllSelectableScreens().length : this.getTabCount("all");
    const folderCount: number = this.countFolders(this.props.sd.project?.screenFolders ?? []);
    const screenNoun: string = this.isReportMode ? "report" : "screen";
    const countText: string = `${screenCount} ${screenNoun}${screenCount === 1 ? "" : "s"} · ${folderCount} ${
      folderCount === 1 ? "folder" : "folders"
    }`;
    return (
      <div className="kuika-picker__footer">
        <span className="kuika-picker__footer-count" title={countText}>
          {countText}
        </span>
        <span
          className="kuika-picker__manage"
          data-testid="subBarManageAllScreens"
          role="button"
          tabIndex={0}
          onClick={this.manageAllScreens}
        >
          <KuikaSvg name="settings" type="sidebar-icons" width={16} height={16} pointer />
          {this.isReportMode ? "Manage all reports" : "Manage all screens"}
        </span>
      </div>
    );
  };

  render() {
    const { sd } = this.props;
    const currentName: string = this.getCurrentScreenName();
    const { isOpen } = this.state;
    // When a component (not the screen root) is selected, the breadcrumb after us provides the
    // "> path" transition, so drop our dropdown chevron then.
    const active = sd.getActiveKuikaComponent();
    const isComponentSelected: boolean = !!(
      active?.tree &&
      active?.kuikaId &&
      active.kuikaId.toString() !== SpecialDIIds.rootDI.toString()
    );
    const { isElementsOpen } = this.state;
    const screenRoot: ITreeItem | undefined = this.getScreenRootItem();
    const elementItems: KuikaDropdownMenuItemType[] = screenRoot ? getTreeItemChildMenuItems(sd, screenRoot) : [];
    const trigger: React.ReactNode = (
      <div
        className="kuika-subbar__screen-trigger"
        data-testid="subBarScreenSelector"
        data-onboarding-id="header_sub-bar_screen-selector"
        // Closing through the trigger releases the row menu first, so the guard in setOpen
        // can never leave the panel stuck open. A click hands the trigger to the screen list.
        onClick={() => {
          this.setState({ isElementsOpen: false });
          if (isOpen) {
            this.closeAfterRowAction();
          } else {
            this.openFromTrigger(isComponentSelected);
          }
        }}
      >
        {getScreenRoleIcon(sd, sd.sdId)}
        <KuikaTooltip content={isElementsOpen ? "" : currentName}>
          <span className="kuika-subbar__screen-name">{currentName}</span>
        </KuikaTooltip>
        {!isComponentSelected && (
          <KuikaSvg name={isOpen ? "chevron-up" : "chevron-down"} type="arrows" width={12} height={12} />
        )}
      </div>
    );
    const screenMenu: React.ReactNode = (
      <KuikaDropdownMenu
        options={[]}
        open={isOpen}
        setOpen={this.setOpen}
        overlayClassName={`kuika-subbar__screen-dropdown${this.isReportMode ? " kuika-subbar__screen-dropdown--no-chips" : ""}`}
        placement="bottomLeft"
        header={this.renderMenuHeader()}
        emptyState={this.renderBody()}
        // Gated on isOpen, not always-on: rc-trigger keeps a popup mounted (just hidden) after
        // it closes, and the row menus inside now own their own visibility. An open row menu
        // renders into its own portal, so a panel that closed without an outside click — the
        // canvas-iframe dismiss, or a row action that navigates — would leave that popup
        // floating over the app. Unmounting the body with the panel retires it too.
        emptyStateCondition={isOpen}
        footer={this.renderFooter()}
      >
        {trigger}
      </KuikaDropdownMenu>
    );

    return (
      <div className="kuika-subbar__breadcrumb">
        {/* Outside the dropdown: the confirmation has to outlive the menu that opened it. */}
        <DeleteModal
          itemName={this.deleteTargetName}
          triggerModal={this.state.isDeleteModalOpen}
          deleteItem={this.confirmDelete}
          deleteType={this.state.deleteTarget?.kind === "folder" ? "folder" : this.designType}
          closeModal={() => this.setState({ isDeleteModalOpen: false, deleteTarget: null })}
        />
        {this.isReportMode && (
          <ReportCreateModal
            visible={this.state.isNewReportModalVisible}
            onClose={() => this.setState({ isNewReportModalVisible: false })}
          />
        )}
        {/* Mounted only while renaming, so each open starts a fresh form on the right folder —
            the shared modal seeds its name through `initialValues`, which a kept-alive instance
            would not pick up for the next folder. */}
        {this.state.renamingFolder && (
          <ScreenFolderAddComponent
            showEditModal
            editingFolderId={this.state.renamingFolder.id}
            editingFolderName={this.state.renamingFolder.name}
            folderParentFolderId={this.state.renamingFolder.parentFolderId ?? null}
            closeEditModal={() => this.setState({ renamingFolder: null })}
            projectId={sd.project?.id ?? ""}
            componentSetId={sd.project?.componentSetObj?.id ?? ""}
          />
        )}
        {elementItems.length > 0 ? (
          // Outside the screen list, not inside its trigger: a portal's clicks bubble through the
          // React tree, so a pick in this menu would otherwise also open the screen list.
          <KuikaDropdownMenu
            options={elementItems}
            open={isElementsOpen && !isOpen}
            setOpen={this.setElementsOpen}
            trigger="hover"
            placement="bottomLeft"
            overlayClassName="kuika_breadcrumb_dropdown"
            header="Elements"
            menuTestId="subBarScreenElementsMenu"
          >
            {screenMenu}
          </KuikaDropdownMenu>
        ) : (
          screenMenu
        )}
      </div>
    );
  }

  // The screen root of the active selection's path. Row-2's breadcrumb hides that node, so the
  // screen name lists its children instead (KOA-18608).
  private getScreenRootItem = (): ITreeItem | undefined => {
    const root: ITreeItem | undefined = this.props.sd.getActiveKuikaComponent()?.tree?.[0];
    return root?.kuikaId?.toString() === SpecialDIIds.rootDI.toString() ? root : undefined;
  };

  // The screen list owns the trigger once it is open: hover must not stack a second menu on it.
  private setElementsOpen = (open: boolean): void => {
    this.setState({ isElementsOpen: open && !this.state.isOpen });
  };

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    // Re-render when the active screen changes, the project (re)loads, or a screen reloads —
    // these are what change the current screen name/id and the screen list.
    return (
      changeReason === ESdChangeReason.Loading ||
      changeReason === ESdChangeReason.LoadProject ||
      changeReason === ESdChangeReason.ScreenReload ||
      // The screen list and sdName are refreshed under this reason (see screen-provider's own
      // note that it is load-bearing for exactly this gate). Without it, a name that arrives
      // after a delete or a rename never reaches the breadcrumb.
      changeReason === ESdChangeReason.OtherSetStates ||
      // Selection changes toggle the dropdown chevron (hidden when a component is selected).
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools
    );
  };
}

const mapDispatchToProps = (dispatch) => {
  return {
    showDomainAccessRequiredModal: (accessName) => dispatch(showDomainAccessRequiredModal(accessName))
  };
};

const subBarScreenSelector = connect(
  null,
  mapDispatchToProps
)(withContext<ISubBarScreenSelectorProps>(withSD<ISubBarScreenSelectorProps>(SubBarScreenSelector)));
export { subBarScreenSelector as SubBarScreenSelector };
